import { Color } from "../../../../constants/V2/color"

import { TableRegex } from "@constants/tables"
import { getFillColorClass, stringToColor } from "@utils/V2/color"

export const extractFromTag = (text: string, tag: string): string => {
  return text
    .replace(new RegExp(`:${tag}\\("?`), "")
    .replace(new RegExp(`"?\\):`), "")
}

export const getThemeColor = (theme: "light" | "dark"): Color => {
  return theme === "light" ? Color.Charcoal : Color.White
}

export const getIconFillClass = (colorName?: string): string => {
  const color = stringToColor(colorName)
  return color ? getFillColorClass(color) : ""
}

export const getCheckmarkStrokeColor = (fillColor?: string): string => {
  return fillColor === "white" ? "stroke-blue" : "stroke-white"
}

export const getDashStrokeColor = (fillColor?: string): string => {
  return fillColor === "white" ? "stroke-black" : "stroke-white"
}

export const getCellBackgroundClass = (
  rowIndex: number,
  totalRows: number,
  theme: "light" | "dark",
  backgroundColor: Color,
  hasButton?: boolean
): string => {
  const isLastRow = rowIndex === totalRows - 1
  const isEvenRow = rowIndex % 2 === 0

  if (isLastRow && hasButton) {
    if (theme === "light") return "bg-white"
    return backgroundColor === Color.Black ? "!bg-black" : "!bg-charcoal"
  }

  if (isEvenRow) {
    return theme === "light" ? "bg-white" : "bg-dark-gray"
  }

  if (theme === "light") return "bg-light-gray"
  return backgroundColor === Color.Black ? "bg-charcoal" : "bg-black"
}

export const parseContent = (
  regexMatch: RegExpMatchArray | null,
  prefixLength: number
): string[] => {
  return regexMatch
    ? regexMatch[0]
        .slice(prefixLength, -2)
        .split(",")
        .map((item) => item.trim())
    : []
}

export const extractPriceData = (item: YocoCom.ConsolidatedCartItemBase) => {
  return {
    priceSubScript: item.priceSubScript,
    priceSideScript: item.priceSideScript,
    promotionSavings: item.savingsAmount ?? 0,
  }
}

export const parseButtonData = (
  buttonMatch: RegExpMatchArray | null
): {
  buttonLabel?: string
  buttonLink?: string | YocoCom.YocoLink
  secondButtonLabel?: string
  secondButtonLink?: string
} => {
  if (!buttonMatch) return {}

  const [, firstLabel, firstLink, secondLabel, secondLink] = buttonMatch
  return {
    buttonLabel: firstLabel,
    buttonLink: firstLink,
    secondButtonLabel: secondLabel,
    secondButtonLink: secondLink,
  }
}

export const parseTextLinkData = (
  textLinkMatch: RegExpMatchArray | null
): {
  textLinkLabel?: string
  textLinkUrl?: string
} => {
  if (!textLinkMatch) return {}

  const [, label, url] = textLinkMatch

  return {
    textLinkLabel: label,
    textLinkUrl: url,
  }
}

export const extractSimplePriceData = (
  item: YocoCom.ConsolidatedCartItemBase
) => {
  return {
    detailLink: item.detailLink,
  }
}

export const getCellRoundingClasses = (
  displayIndex: number,
  maxDesktopColumns: number
): Record<string, boolean> => {
  return {
    "sm:!rounded-r-none": displayIndex === 0,
    "rounded-l-none": displayIndex === 1,
    "sm:!rounded-l-none": displayIndex === maxDesktopColumns - 1,
    "sm:!rounded-none":
      displayIndex > 0 && displayIndex < maxDesktopColumns - 1,
    "rounded-r-none sm:rounded-r-10": displayIndex === 0,
    "rounded-l-none sm:rounded-l-10": displayIndex === maxDesktopColumns - 1,
  }
}

export const matchCellPatterns = (cellData: string) => {
  return {
    isCheckIcon: cellData.match(TableRegex.CHECK_ICON),
    isCrossIcon: cellData.match(TableRegex.CROSS_ICON),
    isCheckIconBlackStroke: cellData.match(TableRegex.CHECK_ICON_BLACK_STROKE),
    isDashIcon: cellData.match(TableRegex.DASH_ICON),
    isList: cellData.match(TableRegex.LIST),
    isPriceFromStoryblokSlug: cellData.match(
      TableRegex.PRICE_FROM_STORYBLOK_SLUG
    ),
    isDetailedPriceFromStoryblokSlug: cellData.match(
      TableRegex.DETAILED_PRICE_FROM_STORYBLOK_SLUG
    ),
    isPrice: cellData.match(TableRegex.PRICE),
    isButton: cellData.match(TableRegex.BUTTON),
    isHeader: cellData.match(TableRegex.HEADER),
    isProduct: cellData.match(TableRegex.PRODUCT),
    isBullet: cellData.match(TableRegex.BULLET),
    isYocoLogo: cellData.match(TableRegex.YOCO_LOGO),
    isTitle: cellData.match(TableRegex.TITLE),
    isTextLink: cellData.match(TableRegex.TEXT_LINK),
    isSimplePriceFromStoryblokSlug: cellData.match(
      TableRegex.SIMPLE_PRICE_FROM_STORYBLOK_SLUG
    ),
  }
}

// Helper functions for table data transformation
export const hasImageRow = (thead: Storyblok.TableCell[]): boolean =>
  !!thead.find((cell) => cell.value.match(/:image\((.*?)\):/))

export const extractColumnImages = (
  thead: Storyblok.TableCell[]
): string[] | undefined => {
  if (!hasImageRow(thead)) return undefined
  return thead
    .map((cell) => cell.value)
    .filter((value, idx) => !(idx === 0 && value === ""))
}

export const extractRowHeadings = (
  thead: Storyblok.TableCell[],
  tbody: Storyblok.TableBody[],
  hasImages: boolean
): string[] => {
  const headings: string[] = []
  if (!hasImages) {
    headings.push(thead[0].value)
  }
  tbody.forEach((row) => headings.push(row.body[0].value))
  return headings
}

export const buildTableData = (
  thead: Storyblok.TableCell[],
  tbody: Storyblok.TableBody[],
  hasImages: boolean
): string[][] => {
  const tableData: string[][] = []

  if (!hasImages) {
    const headerRow = thead.slice(1).map((cell) => cell.value)
    tableData.push(headerRow)
  }

  tbody.forEach((row) => {
    const dataRow = row.body.slice(1).map((cell) => cell.value)
    tableData.push(dataRow)
  })

  return tableData
}

type TableHeadingData = {
  heading: string
  subHeading?: string
}

export const extractTableHeading = (
  data: string[][],
  rowHeadings: string[]
): TableHeadingData | undefined => {
  if (rowHeadings && rowHeadings.length > 0) {
    const firstRowHeading = rowHeadings[0]
    if (firstRowHeading) {
      const matches = matchCellPatterns(firstRowHeading)
      if (matches.isHeader) {
        const headerMatch = matches.isHeader
        const heading = headerMatch[1] || ""
        const subHeading = headerMatch[2] || undefined
        return { heading, subHeading }
      }
    }
  }

  if (data && data.length > 0) {
    for (const cell of data[0]) {
      if (!cell) continue
      const matches = matchCellPatterns(cell)
      if (matches.isHeader) {
        const headerMatch = matches.isHeader
        const heading = headerMatch[1] || ""
        const subHeading = headerMatch[2] || undefined
        return { heading, subHeading }
      }
    }
  }

  return undefined
}
